// Landing screen for Android's "Share to Travel Planner" flow (see
// docs/state-model.md). App.js redeems the share-target token before
// mounting this screen; `payload` is whatever GET /api/tp/share-target/:token
// returned: { title, text, url, hasImage, imageData, mimeType }.
//
// One tap, one step, no waiting: tapping a destination saves something
// immediately (the raw link, or the scan kicked off) and returns the user
// home right away — extraction/scanning finishes in the background and
// updates the saved item in place once it resolves. If extraction comes back
// empty, the raw link stays saved as-is; nothing is ever shown to the user
// about it, since they're already gone by then. This does NOT introduce a
// new save pipeline — it runs the exact same extraction/scan calls IdeaBar
// (ig/link extract) and ScanSheet (scan-screenshot) already make, and saves
// through onAddIdea/onAddBooking (App.js's tripId-parameterized versions of
// handleSaveCustomIdea+select and handleSaveTripDetails — both written to
// tolerate being called seconds later from a closure captured before this
// screen unmounted, see the comment on handleShareAddIdea in App.js), or
// into the same localStorage bucket list HomeScreen's "Import from
// Instagram" writes to.
import React from 'react';
import { PALETTE } from '../theme';
import { Icon, IconBtn, Header } from '../components/UI';
import { fmtDMY, sortByDateStart } from '../utils/trips';
import { isPastTrip } from '../utils/visited';
import { loadBucket, saveBucket } from './HomeScreen';
import { useAuth } from '../contexts/AuthContext';
import { classifySharePayload, sharedLinkFrom } from '../utils/shareTarget';

const rowStyle = {
  background: PALETTE.card, borderRadius: 14, border: `0.5px solid ${PALETTE.ruleSoft}`,
  padding: 16, textAlign: 'left', cursor: 'pointer', display: 'flex',
  alignItems: 'center', justifyContent: 'space-between', gap: 10, width: '100%',
  appearance: 'none',
};

// Same "destination label" logic as HomeScreen's trip list (title override,
// then multi-city leg join, then plain destination) — kept in sync manually
// since HomeScreen doesn't export it as a shared helper either.
function tripLabel(trip) {
  if (trip.title) return trip.title;
  if (trip.legs && trip.legs.length > 1) {
    return trip.legs.map(l => (l.destination || '').split(',')[0].trim()).filter(Boolean).join(' → ');
  }
  return trip.destination || 'Untitled trip';
}

export function ShareInboxScreen({ isMobile, payload, trips, onAddIdea, onAddBooking, onNewTrip, onDone }) {
  const { user } = useAuth();
  const firedRef = React.useRef(false); // guards a double-tap in the instant before this screen unmounts
  const kind = classifySharePayload(payload || {});
  const sourceUrl = sharedLinkFrom(payload || {});
  const upcoming = sortByDateStart(trips.filter(t => !isPastTrip(t)));

  const authedHeaders = async () => {
    const token = user ? await user.getIdToken() : '';
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  };

  // Normalizes ig/extract's {items:[...]} and link/extract's {item:{...}}
  // into one shape both the trip-idea path and the inspiration path consume.
  // Never throws — a failed/empty extraction just means "nothing better to
  // upgrade the placeholder with," not an error anyone needs to see.
  const extractLinkItems = async () => {
    try {
      const headers = await authedHeaders();
      const endpoint = kind === 'instagram' ? '/api/tp/ig/extract' : '/api/tp/link/extract';
      const r = await fetch(endpoint, { method: 'POST', headers, body: JSON.stringify({ url: sourceUrl }) });
      const data = await r.json();
      if (!r.ok) return [];
      if (kind === 'instagram') return data.items || [];
      return data.item?.title ? [data.item] : [];
    } catch { return []; }
  };

  const addToTrip = (tripId) => {
    if (firedRef.current) return;
    firedRef.current = true;

    if (kind === 'image') {
      // No sensible placeholder for a booking before it's scanned — kick the
      // scan off in the background and let the user go immediately either way.
      (async () => {
        try {
          const headers = await authedHeaders();
          const r = await fetch('/api/tp/scan-screenshot', {
            method: 'POST', headers,
            body: JSON.stringify({ imageData: payload.imageData, mimeType: payload.mimeType || 'image/jpeg' }),
          });
          const data = await r.json();
          if (r.ok) onAddBooking(tripId, data);
        } catch { /* best-effort; user is long gone */ }
      })();
      onDone();
      return;
    }

    const id = `share_${Date.now()}`;
    const placeholder = {
      id, title: payload?.title || sourceUrl, cat: 'Experience',
      dur: 2, area: '', meta: '', requiresBooking: false, sourceUrl,
    };
    onAddIdea(tripId, placeholder);
    onDone();
    (async () => {
      const [first, ...rest] = await extractLinkItems();
      if (first) {
        onAddIdea(tripId, {
          id, title: first.title || placeholder.title, cat: first.category || 'Experience',
          dur: 2, area: first.destination || '', meta: first.notes || '', requiresBooking: false, sourceUrl,
        });
        rest.forEach((item, idx) => onAddIdea(tripId, {
          id: `share_${Date.now()}_${idx}`, title: item.title, cat: item.category || 'Experience',
          dur: 2, area: item.destination || '', meta: item.notes || '', requiresBooking: false, sourceUrl,
        }));
      }
      // No items found: the placeholder (raw link as the title) stays as-is.
    })();
  };

  const addToInspiration = () => {
    if (firedRef.current) return;
    firedRef.current = true;

    const id = `share_${Date.now()}`;
    const placeholder = { id, title: payload?.title || sourceUrl, tag: 'someday', destination: '', notes: `Source: ${sourceUrl}` };
    saveBucket([...loadBucket(), placeholder]);
    onDone();
    (async () => {
      const [first, ...rest] = await extractLinkItems();
      if (first) {
        const upgraded = {
          id, title: first.title || placeholder.title, tag: 'someday', destination: first.destination || '',
          notes: `${first.notes ? first.notes + ' · ' : ''}Source: ${sourceUrl}`,
        };
        const restEntries = rest.map(it => ({
          title: it.title, tag: 'someday', destination: it.destination || '',
          notes: `${it.notes ? it.notes + ' · ' : ''}Source: ${sourceUrl}`,
        }));
        // Re-read fresh (not the array captured above) — time has passed and
        // the user may have added other bucket entries in the meantime.
        saveBucket([...loadBucket().map(e => e.id === id ? upgraded : e), ...restEntries]);
      }
    })();
  };

  return (
    <div style={{ minHeight: '100vh', background: PALETTE.paper }}>
      <Header
        isMobile={isMobile}
        left={<IconBtn name="x" onClick={onDone} label="Close" />}
        subtitle="Shared to Travel Planner"
        title="Add from share"
      />
      {!payload || kind === 'none' ? (
        <div style={{ padding: '4px 20px 40px', fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkSoft }}>
          Nothing to add — that share didn't include a link or an image.
        </div>
      ) : (
        <div style={{ padding: '4px 20px 40px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkSoft }}>Add to which trip?</div>
          {kind !== 'image' && (
            <button onClick={addToInspiration} style={rowStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Icon name="star" size={16} style={{ color: PALETTE.accent, flexShrink: 0 }} />
                <span style={{ fontFamily: 'Fraunces', fontSize: 17, color: PALETTE.ink }}>Travel Inspiration</span>
              </div>
              <Icon name="arrow" size={16} style={{ color: PALETTE.inkMute, flexShrink: 0 }} />
            </button>
          )}
          {upcoming.length === 0 ? (
            <div style={{ ...rowStyle, cursor: 'default', flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
              <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkSoft }}>
                {trips.length === 0 ? "You don't have any trips yet." : 'No upcoming trips — your trips are all in the past.'}
              </div>
              <button onClick={onNewTrip} style={{ appearance: 'none', border: 0, background: PALETTE.accent, color: '#fff', borderRadius: 8, padding: '8px 0', fontFamily: 'Inter', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Create a trip</button>
            </div>
          ) : upcoming.map(trip => (
            <button key={trip.id} onClick={() => addToTrip(trip.id)} style={rowStyle}>
              <div>
                <div style={{ fontFamily: 'Fraunces', fontSize: 17, color: PALETTE.ink }}>{tripLabel(trip)}</div>
                {trip.dateStart && (
                  <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, marginTop: 3 }}>
                    {fmtDMY(trip.dateStart)}{trip.dateEnd ? ` – ${fmtDMY(trip.dateEnd)}` : ''}
                  </div>
                )}
              </div>
              <Icon name="arrow" size={16} style={{ color: PALETTE.inkMute, flexShrink: 0 }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
